import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, Pause, X, SkipBack, SkipForward } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { BeatCover } from "@/components/BeatCover";
import { Waveform, fmt } from "@/components/Waveform";

export default function MiniPlayer() {
  const { track, playing, time, dur, toggle, seek, close, next, prev, queueSize } = usePlayer();
  const pct = dur ? (time / dur) * 100 : 0;

  return (
    <AnimatePresence>
      {track && (
        <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 260 }}
          className="fixed bottom-0 inset-x-0 z-[95] flex justify-center px-0 md:px-4 md:pb-4" data-testid="mini-player">
          <div className="w-full max-w-[1200px] md:max-w-[900px] bg-[#0D0D0D]/90 backdrop-blur-xl border-t md:border border-white/10 shadow-[0_-10px_50px_rgba(217,4,41,0.25)]">
            <div className="h-[2px] bg-white/10 md:hidden"><div className="h-full bg-[#D90429]" style={{ width: `${pct}%` }} /></div>
            <div className="flex items-center gap-3 px-3 md:px-4 py-2.5">
              <BeatCover beat={track} className="h-10 w-10" testId="mini-player-cover" />
              <div className="min-w-0 flex-1 md:flex-none md:w-48">
                <Link to="/beats" className="block text-sm font-bold truncate hover:text-[#D90429]" data-testid="mini-player-title">{track.title}</Link>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#D90429]">{playing ? "Now playing" : "Paused"}</p>
              </div>
              <div className="hidden md:flex items-center gap-3 flex-1">
                <span className="text-[11px] font-mono text-white/50 w-9 text-right">{fmt(time)}</span>
                <Waveform pct={pct} active={playing} onSeek={seek} testId="mini-player-progress" bars={40} />
                <span className="text-[11px] font-mono text-white/50 w-9">{fmt(dur)}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={prev} data-testid="mini-player-prev" aria-label="Previous beat"
                  className="h-8 w-8 flex items-center justify-center text-white/60 hover:text-white transition-colors">
                  <SkipBack className="h-4 w-4" fill="currentColor" />
                </button>
                <button onClick={() => toggle()} data-testid="mini-player-toggle" aria-label={playing ? "Pause" : "Play"}
                  className="h-10 w-10 shrink-0 rounded-full bg-[#D90429] hover:bg-[#ED2939] text-white flex items-center justify-center shadow-[0_0_20px_rgba(217,4,41,0.5)] transition-colors">
                  {playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4 ml-0.5" fill="currentColor" />}
                </button>
                <button onClick={next} disabled={!queueSize} data-testid="mini-player-next" aria-label="Next beat"
                  className="h-8 w-8 flex items-center justify-center text-white/60 hover:text-white disabled:opacity-30 transition-colors">
                  <SkipForward className="h-4 w-4" fill="currentColor" />
                </button>
              </div>
              <button onClick={close} data-testid="mini-player-close" aria-label="Close player"
                className="h-8 w-8 shrink-0 flex items-center justify-center text-white/40 hover:text-white transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
